/**
 * Adult authentication. There is exactly one kind of session in this app and it
 * belongs to a guardian.
 *
 * Children never sign in — no email, no password, no magic link, no auth row.
 * A child is a profile under this session, switched into with a PIN
 * (`useProfile`). Keeping that line sharp is most of the COPPA posture
 * (PLAN_REVIEW §8), and it is why this file has no concept of a child at all.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isConfigured } from './supabase.js';

const AuthContext = createContext(null);

const OAUTH_PROVIDERS = (import.meta.env.VITE_OAUTH_PROVIDERS ?? '')
  .split(',').map((p) => p.trim()).filter((p) => p === 'google' || p === 'apple');

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(isConfigured);

  useEffect(() => {
    if (!isConfigured) return undefined;

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user ?? null,
    loading,
    isConfigured,
    /** Magic link only. A household password is a password five people know. */
    signIn: (email) => supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    }),
    /**
     * OAuth is a redirect, so the browser leaves and comes back to `redirectTo`;
     * that URL must be in the project's redirect allow-list. Which providers are
     * offered is a build-time decision (`VITE_OAUTH_PROVIDERS`), because a button
     * for a provider the project has not been given credentials for is a dead end.
     */
    signInWith: (provider) => supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    }),
    oauthProviders: OAUTH_PROVIDERS,
    signOut: () => supabase.auth.signOut(),
  }), [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
